(ns common.widgets.datepicker
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/ClickAwayListener$default" :as ClickAwayListener]
            ["@mui/material/Grid$default" :as Grid]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/InputAdornment$default" :as InputAdornment]
            ["@mui/material/Paper$default" :as Paper]
            ["@mui/material/Popper$default" :as Popper]
            ["@mui/material/Tab$default" :as Tab]
            ["@mui/material/Tabs$default" :as Tabs]
            ["@mui/x-date-pickers/DateCalendar" :refer [DateCalendar]]
            ["@mui/x-date-pickers/DateField" :refer [DateField]]
            ["@mui/x-date-pickers/DatePicker" :refer [DatePicker]]
            ["@mui/x-date-pickers/DateTimePicker" :refer [DateTimePicker]]
            ["@mui/x-date-pickers/TimePicker" :refer [TimePicker]]
            ["moment/min/moment-with-locales$default" :as moment]
            ["react" :as react]
            [cljs-bean.core :refer [->js]]
            [clojure.string :as str]
            [common.util :as util]
            [common.widgets.checkbox :refer [checkbox]]
            [common.widgets.dropdown :refer [dropdown]]
            [common.widgets.input :refer [input]]
            [reagent.core :as r]
            [uix.core :as uix :refer [$ defui]]))

(def relative-options
  [{:label "Now"
    :value "now"}
   {:label "Minutes ago"
    :value "-%m"}
   {:label "Hours ago"
    :value "-%H"}
   {:label "Days ago"
    :value "-%d"}
   {:label "Weeks ago"
    :value "-%w"}
   {:label "Months ago"
    :value "-%M"}
   {:label "Years ago"
    :value "-%y"}
   {:label "Minutes from now"
    :value "+%m"}
   {:label "Hours from now"
    :value "+%H"}
   {:label "Days from now"
    :value "+%d"}
   {:label "Weeks from now"
    :value "+%w"}
   {:label "Months from now"
    :value "+%M"}
   {:label "Years from now"
    :value "+%y"}])

(def type-map
  {"m" "minute"
   "H" "hour"
   "d" "day"
   "w" "week"
   "M" "month"
   "y" "year"})

(defn parse-relative-datetime
  [^js datetime relative-type amount round?]
  (let [date-type (get type-map (last relative-type))]
    (if (= relative-type "now")
      datetime
      (let [^js date (if (= "-" (first relative-type))
                       (.subtract datetime amount (str date-type "s"))
                       (.add datetime amount (str date-type "s")))]
        (if round?
          (.startOf date date-type)
          date)))))

(defn external-string->amount
  "e.g.: now-6M -> 6"
  [s]
  (if (= s "now")
    "now"
    (re-find #"[0-9]+" s)))

(defn external-string->type
  "e.g.: now-6M -> month"
  [s]
  (if (= s "now")
    "now"
    (get type-map (last s))))

(defn external-string->relative-type
  "e.g.: now-6M -> -%M"
  [s]
  (if (or (= s "now") (str/blank? s))
    "now"
    (str (nth s 3) "%" (last s))))

(defn date-preview
  [amount relative-type round?]
  (-> (moment)
      (parse-relative-datetime relative-type amount round?)
      (util/format-iso-string "LLLL")))

(defn external-string->readable
  [s]
  (let [amount (external-string->amount s)
        type (external-string->type s)
        future? (and (not= s "now")
                     (= "+" (nth s 3)))
        rounded? (= "/" (second (reverse s)))]
    (if (= s "now")
      "now"
      (str "~" amount " "
           type "s"
           (if future? " from now" " ago")
           (when rounded? (str " (rounded down to nearest " type ")"))))))

(defn internal-value->external-value
  [amount type round?]
  (if (= type "now")
    "now"
    (str
     "now"
     (str/replace type #"%" (str amount))
     (when round?
       (str "/" (last type))))))

(defn external-value->internal-value
  [value]
  {:amount (or (external-string->amount value) 0)
   :round? (= "/" (second (reverse value)))
   :relative-type (or (external-string->relative-type value) "now")})

(defui relative-picker
  [{:keys [value on-change close-popper]}]
  (let [internal-value (external-value->internal-value value)
        [amount set-amount] (react/useState (:amount internal-value))
        [round? set-round?] (react/useState (:round? internal-value))
        [relative-type set-relative-type] (react/useState (:relative-type internal-value))]
    ($ Grid {:container true :spacing 1 :p 2 :width "320px"}
       ($ Grid {:size #js {:xs 3}}
          ($ input
             {:value amount
              :data-testid "datepicker-relative-amount"
              :disabled (= relative-type "now")
              :inputProps #js {:min 0}
              :type "number"
              :onChange #(set-amount (-> % .-target .-value int))}))
       ($ Grid {:size #js {:xs 9}}
          (r/as-element
           [dropdown {:value relative-type
                      :on-change #(set-relative-type %)
                      :options relative-options
                      :data-testid "datepicker-relative-operator"}]))
       ($ Grid {:size #js {:xs 12}}
          ($ checkbox
             {:id :relative-datetime
              :disabled? (= relative-type "now")
              :label (str "Round down to nearest " (get type-map (last relative-type)))
              :on-change #(set-round? %)
              :value round?}))
       ($ Grid {:size #js {:xs 12}}
          ($ :p
             "Date preview: "
             ($ :b (date-preview amount relative-type round?))))
       ($ Grid {:size #js {:xs 12} :display "flex" :justifyContent "flex-end"}
          ($ Button
             {:variant "contained"
              :data-testid "datepicker-relative-submit"
              :onClick #(do (close-popper)
                            (on-change (internal-value->external-value amount relative-type round?)))}
             "OK")))))

(defui mixed-input [{:keys [value-fn on-change active-tab set-anchor-el] :as props}]
  (let [text-field-props (dissoc props :value-fn :active-tab :set-anchor-el)
        input-adornment ($ InputAdornment {:position "end"}
                           ($ IconButton {:onClick #(set-anchor-el
                                                     (.closest (.. % -currentTarget) "[role='presentation'"))}
                              ($ Icon "event")))
        input-props {:endAdornment input-adornment
                     :size "small"
                     :sx #js {:pr 0}}]
    (if (= active-tab :fixed)
      ($ DateField
         (merge (dissoc text-field-props :on-change)
                {:onChange #(on-change (value-fn %))
                 :InputProps (->js input-props)
                 :fullWidth true}))
      ($ input
         (merge text-field-props
                {:readOnly true}
                input-props)))))

(defui mixed-date-picker
  [{:keys [value value-fn on-change] :as props
    :or {value-fn identity}}]
  (let [[anchor-el set-anchor-el] (uix/use-state nil)
        [active-tab set-active-tab] (uix/use-state (if (util/valid-elasticsearch-date-string? value) :relative :fixed))

        close-popper #(set-anchor-el nil)

        input-value (if (= active-tab :fixed)
                      (let [m (moment value)]
                        (when (.isValid m) m))
                      (cond
                        (not (string? value)) nil
                        (util/valid-elasticsearch-date-string? value) (external-string->readable value)
                        :else (util/format-iso-string value "L")))]
    ($ ClickAwayListener {:on-click-away close-popper}
       ($ :div {:role "presentation"}
          ($ mixed-input (merge props {:value input-value
                                       :active-tab active-tab
                                       :set-anchor-el set-anchor-el}))
          ($ Popper {:open (boolean anchor-el)
                     :anchorEl anchor-el
                     :sx #js {:zIndex 1300}}
             ($ Paper {:elevation 2}
                ($ Tabs {:value active-tab
                         :onChange (fn [_e v] (set-active-tab (keyword v)))}
                   ($ Tab {:value :fixed :label "Fixed"})
                   ($ Tab {:value :relative :label "Relative"}))

                (if (= active-tab :fixed)
                  ($ DateCalendar {:value (let [m (moment value)]
                                            (when (.isValid m) m))
                                   :onChange #(on-change (value-fn %))})
                  ($ relative-picker {:value value :on-change on-change :close-popper close-popper}))))))))

(defui datetime-picker
  [{:keys [value onBlur onChange error slotProps] :as props}]
  ($ DateTimePicker
     (-> props
         (dissoc :value :onBlur :onChange :error :slotProps)
         (assoc :value (moment value)
                :onChange (fn [selected-date]
                            (onChange (when (some? selected-date)
                                        (.toISOString selected-date))))
                :timeSteps #js {:hours 1 :minutes 1}
                :slotProps (util/spread-props slotProps
                                              #js {:textField
                                                   #js {:error error
                                                        :onFocus (:onFocus props)
                                                        :InputProps #js {:onBlur onBlur}}})))))

(defui time-picker
  [{:keys [error onBlur slotProps textFieldProps] :as props}]
  ($ TimePicker
     (merge (dissoc props :onBlur :error :slotProps)
            {:slotProps (util/spread-props slotProps
                                           #js {:textField
                                                (util/spread-obj #js {:error error
                                                                      :InputProps #js {:onBlur onBlur}}
                                                                 textFieldProps)})})))
